<template>
  <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
    <div
      v-for="(report, index) in reports"
      :key="index"
      class="group rounded-xl border border-slate-200/60 bg-white shadow-sm transition-all duration-200 hover:border-slate-300/80 hover:shadow-lg dark:border-slate-700/60 dark:bg-slate-900/80 dark:hover:border-slate-600/80"
    >
      <!-- Header -->
      <div class="border-b border-slate-200/60 p-4 dark:border-slate-700/60">
        <div class="flex items-start justify-between gap-3">
          <div class="flex-1">
            <div class="flex items-center gap-2 mb-2">
              <span
                class="inline-flex items-center justify-center rounded-lg bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-300"
              >
                #{{ report.session_number }}
              </span>
              <span class="text-xs text-slate-500 dark:text-slate-400">
                {{ report.total_transactions }} transacciones
              </span>
            </div>
            <div class="space-y-1">
              <div class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                <svg
                  class="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                {{ formatDateTime(report.opened_at) }}
              </div>
              <div
                v-if="report.closed_at"
                class="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400"
              >
                <svg
                  class="h-3.5 w-3.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{ formatDateTime(report.closed_at) }}
              </div>
            </div>
          </div>
          <BaseButton
            variant="ghost"
            size="sm"
            class="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            @click="$emit('view-details', report.session_id)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
              <path
                fill-rule="evenodd"
                d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                clip-rule="evenodd"
              />
            </svg>
          </BaseButton>
        </div>
      </div>

      <!-- Metrics -->
      <div class="p-4 space-y-3">
        <div class="grid grid-cols-1 gap-3">
          <div
            class="flex items-center justify-between rounded-lg bg-emerald-50/50 px-3 py-2 dark:bg-emerald-950/20"
          >
            <span class="text-xs font-medium text-emerald-700 dark:text-emerald-300">Ventas</span>
            <span class="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{{
              formatCurrency(report.total_sales || 0) }}</span>
          </div>

          <div class="flex items-center justify-between rounded-lg bg-amber-50/50 px-3 py-2 dark:bg-amber-950/20">
            <span class="text-xs font-medium text-amber-700 dark:text-amber-300">Gastos</span>
            <span class="text-sm font-semibold text-amber-600 dark:text-amber-400">{{
              formatCurrency(report.total_expenses || 0) }}</span>
          </div>

          <div
            class="flex items-center justify-between rounded-lg border px-3 py-2"
            :class="(report.net_cash_flow || 0) >= 0 ? 'bg-sky-50/50 border-sky-200/60 dark:bg-sky-950/20 dark:border-sky-800/60' : 'bg-red-50/50 border-red-200/60 dark:bg-red-950/20 dark:border-red-800/60'"
          >
            <span
              class="text-xs font-medium"
              :class="(report.net_cash_flow || 0) >= 0 ? 'text-sky-700 dark:text-sky-300' : 'text-red-700 dark:text-red-300'"
            >Flujo
              neto</span>
            <span
              class="text-sm font-semibold"
              :class="(report.net_cash_flow || 0) >= 0 ? 'text-sky-600 dark:text-sky-400' : 'text-red-600 dark:text-red-400'"
            >{{
              formatCurrency(report.net_cash_flow || 0) }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import BaseButton from '@/components/ui/BaseButton.vue'

interface Report {
  session_number: number
  session_id: number
  total_transactions: number
  opened_at: string
  closed_at?: string
  total_sales?: number
  total_expenses?: number
  net_cash_flow?: number
}

interface Props {
  reports: Report[]
}

defineProps<Props>()

defineEmits<{
  'view-details': [sessionId: number]
}>()

const formatDateTime = (dateString: string) => {
  if (!dateString) return 'N/A'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch (error) {
    return 'Invalid date'
  }
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
  }).format(amount)
}
</script>
